import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../types/common/types';
import { GzListItemWebComponent } from '../../../ui/gzListItem';
import {
  Categories,
  GenericGridSmartViewsWebComponentSchema,
  InitialView,
  InternalCategory,
  SmartViewCategory,
  SmartViewInlineActions,
  SmartViewItemConfiguration,
  SmartViewState,
  SmartViewType,
  ValidateViewNameErrorCodeResponse,
  ValidateViewNameResponse,
} from '../types';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../../ui/gzForm/validators/remote.validator';
import _ from 'lodash';
import { GenericSmartViewsListWebComponent } from '../../genericSmartViewsList';

export class GenericGridSmartViewsView extends BaseWebComponentView {
  public activeItem: GzListItemWebComponent;
  public categories: Array<SmartViewCategory> = [];
  public smartViews: Array<SmartViewItemConfiguration> = [];
  protected inlineActionsStrings: any;
  protected smartViewsStrings: any;
  public smartViewState: SmartViewState;
  public activeView: SmartViewItemConfiguration | undefined;
  private internalCategoriesConfig;
  public initialView;
  private isFirstLoad = true;
  private customErrorMappingFn: (errorCode: number) => string | null;
  public focusOnSelectedView = false;

  public getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper') as HTMLElement;
    this.dom.$smartViewsLit = this.shadow.getElementById('genericSmartViewsList') as GenericSmartViewsListWebComponent;
    this.setTranslatedStrings();
    this.getTranslations();
  }

  getTranslations() {
    return {
      inlineActionsStrings: this.inlineActionsStrings,
      smartViewsStrings: this.smartViewsStrings,
    };
  }

  setTranslatedStrings() {
    this.inlineActionsStrings = this.stringsTranslator.getInlineActionsCommonStrings();
    this.smartViewsStrings = this.stringsTranslator.getSmartViewsTranslations();
  }

  /**
   * initializes an active view if not present
   * will build all categories
   */
  public buildSuccessView(data: GenericGridSmartViewsWebComponentSchema) {
    this.categories = [];
    this.smartViewState = data.settings;
    this.smartViews = data.data;
    this.internalCategoriesConfig = data.categoriesConfig.internalCategories;
    this.customErrorMappingFn = data.customErrorMappingFn;

    this.initialView = this.computeInitialView(data.initialView);
    if (this.initialView) {
      if (this.initialView.isActiveViewOnLoad && this.isFirstLoad) {
        this.smartViewState.selectedViewId = this.initialView.view._id;
      }
      this.smartViews.push(this.initialView.view);
    }
    this.initializeActiveViewIfNotPresent();

    const hasFavorites = data.categoriesConfig.internalCategories.favorites.isAvailable;
    const hasRecent = data.categoriesConfig.internalCategories.recent.isAvailable;
    const hasDefault = data.categoriesConfig.internalCategories.default.isAvailable;
    if (hasRecent) {
      const recentCategory = this.buildRecentCategory(data.data, data.categoriesConfig.internalCategories.recent);
      this.categories.push(recentCategory);
    }
    const customCategories = this.buildCustomCategories(data.data, data.categoriesConfig.customCategories);
    this.categories.push(...customCategories);
    if (hasFavorites) {
      const favoriteCategory = this.buildFavoriteCategory(
        data.data,
        data.categoriesConfig.internalCategories.favorites
      );
      this.categories.push(favoriteCategory);
    }
    if (hasDefault) {
      this.categories.push(
        this.buildDefaultCategory(
          data.data,
          data.categoriesConfig.internalCategories.default,
          data.categoriesConfig.internalCategories.default
        )
      );
    }

    this.dom.$smartViewsLit.translations = {
      emptySavedViewsText: data.emptySavedViewsText
        ? data.emptySavedViewsText
        : this.getTranslations().smartViewsStrings.emptySavedViewsText,
      searchPlaceholderString: data?.searchPlaceholderString
        ? data.searchPlaceholderString
        : this.dependencies.translateFn('Search views'),
    };
    if (this.initialView) {
      this.addInitialViewToMatchingCategory(this.categories, this.initialView);
    }
    this.dom.$smartViewsLit.categories = _.cloneDeep(this.categories);
    this.dom.$smartViewsLit.hasInputSearch = data.hasSearchInput;
    if (typeof data.searchFn === 'function') {
      this.dom.$smartViewsLit.searchFn = data.searchFn;
    }
    if (typeof data.decoratedTextOnSearchFn === 'function') {
      this.dom.$smartViewsLit.decoratedTextOnSearchFn = data.decoratedTextOnSearchFn;
    }
    if (this.focusOnSelectedView) {
      this.dom.$smartViewsLit.updateComplete.then((): void => this.dom.$smartViewsLit.focusOnActiveItem());
    }
    this.isFirstLoad = false;
    this.focusOnSelectedView = false;
  }

  appendExtraDataToView(view) {
    return {
      ...view,
      selectDisabled: false,
      editable: false,
      active: view._id === this.smartViewState.selectedViewId,
    };
  }

  private addInitialViewToMatchingCategory(categories: SmartViewCategory[], initialView: InitialView): void {
    for (const smartViewsList of categories) {
      if (smartViewsList.type === initialView.category) {
        smartViewsList.items.unshift(<SmartViewItemConfiguration>initialView.view);
        break;
      }
    }
  }

  private computeInitialView(initialView: InitialView | undefined) {
    if (!initialView) {
      return null;
    }
    const constructedInitialView = {
      isActiveViewOnLoad: initialView.isActiveViewOnLoad,
      view: {
        ...{
          _id: 'default_view',
          componentName: '',
          label: this.dependencies.translateFn('New view'),
          userId: '',
          type: SmartViewType.GRID_VIEW,
          isReadOnly: true,
          isFavorite: false,
          isRecent: false,
          isInitial: true,
          active: false,
          specifics: {},
        },

        ...initialView.view,
      },
      category: initialView.category || Categories.DEFAULT
    };

    constructedInitialView.view.active =
      (this.isFirstLoad && constructedInitialView.isActiveViewOnLoad) ||
      constructedInitialView.view._id === this.smartViewState.selectedViewId;

    return constructedInitialView;
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  /**
   * initializes an active view if it's not set or it was deleted
   */
  initializeActiveViewIfNotPresent() {
    const smartViewsIds = this.extractColumn(this.smartViews, '_id');
    const currentViewId = this.smartViewState.selectedViewId;
    if (!currentViewId || !smartViewsIds.includes(currentViewId)) {
      const defaultView = this.getReadOnlyView(this.smartViews, this.internalCategoriesConfig.default);
      this.smartViewState.selectedViewId = defaultView._id;
      this.activeView = defaultView;
    } else {
      let activeView = this.smartViews.find(view => {
        return view._id === currentViewId;
      });
      if (activeView?.isReadOnly) {
        activeView = {
          ...activeView,
          label: this.internalCategoriesConfig.default.translations[activeView.label]
            ? this.internalCategoriesConfig.default.translations[activeView.label]
            : activeView.label,
        };
      }
      this.activeView = activeView;
    }
  }

  extractColumn(arr, column) {
    return arr.map(x => x[column]);
  }

  /**
   * we can have multiple default categories
   * these should be inserted with an update script
   */
  buildDefaultCategory(
    data: Array<SmartViewItemConfiguration>,
    defaultCategoryConfig,
    categoryConfig: InternalCategory
  ) {
    const defaultCategoryItems = data.filter(view => {
      return view.isReadOnly && view.category === Categories.DEFAULT;
    });

    const modifiedViews = defaultCategoryItems.map(view => {
      return {
        ...view,
        originalLabel: view.label,
        label: defaultCategoryConfig.translations[view.label]
          ? defaultCategoryConfig.translations[view.label]
          : view.label,
        selectDisabled: false,
        editable: false,
        isInitial: false,
        active: view._id === this.smartViewState.selectedViewId,
      };
    });

    const sectionTitle = categoryConfig.sectionTitle
      ? categoryConfig.sectionTitle
      : this.dependencies.translateFn('DEFAULTS');
    return {
      sectionTitle: sectionTitle,
      type: Categories.DEFAULT,
      inlineActions: [],
      smartViewMaxInputLength: 64,
      noSpaceEscape: true,
      smartViewValidators: [RequiredValidator('This field is required.')],
      isOpen: this.computeIsOpen(modifiedViews, categoryConfig.isOpen, true),
      items: modifiedViews,
    };
  }

  /**
   * views that are not favorite/recent/and default(with category = default)
   */
  buildCustomCategories(customViews: Array<SmartViewItemConfiguration>, categoriesConfig) {
    const customCategoryItems = customViews.filter(view => {
      return !view.isFavorite && !view.isRecent && view.category !== Categories.DEFAULT;
    });
    const categoryViews = this.buildCategoryViewsMap(customCategoryItems, categoriesConfig);
    const customCategories: Array<SmartViewCategory> = [];
    for (const [category, views] of Object.entries(categoryViews)) {
      customCategories.push(this.getProperties(views, category, categoriesConfig));
    }
    return customCategories;
  }

  /**
   * will build a category from a category config and
   * the views that belong to that category
   */
  getProperties(views, key, categoriesConfig: Array<SmartViewCategory>): SmartViewCategory {
    const categoryConfig = categoriesConfig.find(config => {
      if (config.type === key) {
        return config;
      }
    });
    if (!categoryConfig) {
      throw Error('Missing category config');
    }
    const modifiedViews = views.map(view => {
      return this.appendExtraDataToView(view);
    });

    const emptyStateItemText = categoryConfig.emptyStateItemText
      ? categoryConfig.emptyStateItemText
      : this.dependencies.translateFn('No views yet');

    return {
      sectionTitle: categoryConfig.sectionTitle,
      type: categoryConfig.type,
      inlineActions: categoryConfig.inlineActions,
      smartViewMaxInputLength: categoryConfig.smartViewMaxInputLength,
      smartViewValidators: categoryConfig.smartViewValidators,
      noSpaceEscape: categoryConfig.noSpaceEscape,
      emptyStateItemText: emptyStateItemText,
      isOpen: this.computeIsOpen(modifiedViews, categoryConfig.isOpen, true),
      items: modifiedViews,
    };
  }

  getReadOnlyView(views, defaultCategoryConfig) {
    const view = this.initialView?.view
      ? this.initialView.view
      : views.find(e => e.isReadOnly === true && e.category === 'default');
    return {
      ...view,
      originalLabel: view.label,
      label: defaultCategoryConfig.translations[view.label],
      selectDisabled: false,
      editable: false,
      active: true,
    };
  }

  setDefaultViewAsActiveView() {
    const defaultView = this.getReadOnlyView(this.smartViews, this.internalCategoriesConfig.default);
    this.smartViewState.selectedViewId = defaultView._id;
    this.activeView = defaultView;
  }

  buildCategoryViewsMap(
    views: Array<SmartViewItemConfiguration>,
    categoriesConfig
  ): Record<string, Array<SmartViewItemConfiguration>> {
    const map: Record<string, Array<SmartViewItemConfiguration>> = {};
    for (const categoryConfig of categoriesConfig) {
      if (!map[categoryConfig.type]) {
        map[categoryConfig.type] = [];
      }
      map[categoryConfig.type] = views.filter(view => {
        return view.category === categoryConfig.type;
      });
    }
    return map;
  }

  private buildFavoriteCategory(
    data: Array<SmartViewItemConfiguration>,
    categoryConfig: InternalCategory
  ): SmartViewCategory {
    const favoriteCategoryItems = data.filter(view => {
      return view.isFavorite === true;
    });
    const modifiedViews = favoriteCategoryItems.map(view => {
      return this.appendExtraDataToView(view);
    });
    const sectionTitle = categoryConfig.sectionTitle
      ? categoryConfig.sectionTitle
      : this.dependencies.translateFn('FAVORITES');
    return {
      sectionTitle: sectionTitle,
      type: Categories.FAVORITES,
      inlineActions: [
        {
          title: this.dependencies.translateFn('Rename'),
          value: SmartViewInlineActions.RENAME,
        },
        {
          title: this.dependencies.translateFn('Delete'),
          value: SmartViewInlineActions.DELETE,
          customClass: 'danger',
        },
      ],
      smartViewMaxInputLength: 64,
      noSpaceEscape: true,
      emptyStateItemText: categoryConfig.emptyStateItemText
        ? categoryConfig.emptyStateItemText
        : this.dependencies.translateFn('No favorite views yet'),
      isOpen: this.computeIsOpen(modifiedViews, categoryConfig.isOpen, true),
      smartViewValidators: [RequiredValidator('This field is required.'), this.getRemoteValidator()],
      items: modifiedViews,
    };
  }

  private getRemoteValidator() {
    return RemoteValidator(
      (value: string) => {
        const viewName = value.trimEnd().trimStart();

        return this.dependencies.api.clients.SmartViews.validateViewName(this.dependencies.componentName, {
          name: viewName,
        });
      },
      (response: ValidateViewNameResponse) => response.valid,
      (response: ValidateViewNameResponse) => this.translateValidateViewNameErrorCode(response.errorCode),
      this.dependencies.translateFn('A communication error has occurred. Please try again later.')
    );
  }

  private buildRecentCategory(
    data: Array<SmartViewItemConfiguration>,
    categoryConfig: InternalCategory
  ): SmartViewCategory {
    const recentCategoryItems = data.filter(e => {
      return e.isRecent === true;
    });
    const modifiedViews = recentCategoryItems.map(view => {
      return this.appendExtraDataToView(view);
    });

    const sectionTitle = categoryConfig.sectionTitle
      ? categoryConfig.sectionTitle
      : this.dependencies.translateFn('RECENT');
    return {
      sectionTitle: sectionTitle,
      type: Categories.RECENT,
      inlineActions: [],
      smartViewMaxInputLength: 64,
      noSpaceEscape: true,
      emptyStateItemText: categoryConfig.emptyStateItemText
        ? categoryConfig.emptyStateItemText
        : this.dependencies.translateFn('No recent views yet'),
      isOpen: this.computeIsOpen(modifiedViews, categoryConfig.isOpen, false),
      smartViewValidators: [
        RequiredValidator(this.dependencies.translateFn('This field is required.')),
        this.getRemoteValidator(),
      ],
      items: modifiedViews,
    };
  }

  private computeIsOpen(views: Array<any>, categoryConfigIsOpened: boolean | undefined, defaultUnsetIsOpened: boolean = true): boolean | undefined {
    let isOpened;

    if (this.focusOnSelectedView && views.filter((view: any): boolean => view.active).length) {
      isOpened = true;
    } else if (categoryConfigIsOpened !== undefined) {
      isOpened = categoryConfigIsOpened;
    } else {
      isOpened = defaultUnsetIsOpened;
    }

    return isOpened ? true : undefined;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  /**
   *translates an error code to a human readable text
   */
  translateValidateViewNameErrorCode(errorCode: number) {
    const errorString = typeof this.customErrorMappingFn !== 'undefined' ? this.customErrorMappingFn(errorCode) : null;

    if (errorString) {
      return errorString;
    }

    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_LENGTH_CODE) {
      return this.dependencies.translateFn('A view name must have a maximum of 64 characters.');
    }
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_DUPLICATE_NAME_CODE) {
      return this.dependencies.translateFn('A view with this name already exists.');
    }
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_INVALID_CHARACTERS) {
      return this.dependencies.translateFn('A view name must not contain special characters.');
    }

    return null;
  }

  updateListItemData(itemData) {
    this.categories.forEach(category => {
      category.items = category.items.map(view => {
        if (view._id === itemData._id) {
          return this.appendExtraDataToView(itemData);
        } else {
          return this.appendExtraDataToView(view);
        }
      });
    });
    this.dom.$smartViewsLit.categories = _.cloneDeep(this.categories);
  }
}
